// SPDX-License-Identifier: GPL-3.0-or-later
// The five stages of a run, their goals and their twists. Pure data and pure functions.
// The values are the run design section 2. Every value is fixed and public.

import type { RunSignals, StageDef, StageGoal, StageGoalKind, StageTwist } from "./types.ts";

/** The number of stages in one run. */
export const STAGE_COUNT = 5;

/** The stages of a run, in play order. The geometry of the table does not change. */
export const STAGES: readonly StageDef[] = [
  {
    id: "boot",
    number: 1,
    label: "BOOT",
    goal: { kind: "score", target: 300_000 },
    litShots: ["rampLeft", "rampRight"],
    hurryShot: "rampRight",
    twist: { kind: "launch_save", ms: 15_000 },
    twistText: "Long ball save: the launch ball save lasts 15 s.",
  },
  {
    id: "staging",
    number: 2,
    label: "STAGING",
    goal: { kind: "modes", target: 1 },
    litShots: ["orbitLeft", "orbitRight"],
    hurryShot: "rampLeft",
    twist: { kind: "mode_time", scale: 0.85 },
    twistText: "Fast modes: each timed mode is 15% shorter.",
  },
  {
    id: "canary",
    number: 3,
    label: "CANARY",
    goal: { kind: "multiball", target: 1 },
    litShots: ["rampLeft", "orbitLeft"],
    hurryShot: "rampRight",
    twist: { kind: "launch_save", ms: 5_000 },
    twistText: "Short ball save: the launch ball save lasts 5 s.",
  },
  {
    id: "rollout",
    number: 4,
    label: "ROLLOUT",
    goal: { kind: "modes", target: 2 },
    litShots: ["rampLeft", "rampRight", "orbitLeft", "orbitRight"],
    hurryShot: "rampLeft",
    twist: { kind: "shot_value", scale: 1.25 },
    twistText: "High values: ramps and orbits score 25% more.",
  },
  {
    id: "production",
    number: 5,
    label: "PRODUCTION",
    goal: { kind: "score", target: 1_500_000 },
    litShots: ["rampRight", "orbitRight"],
    hurryShot: "rampRight",
    twist: { kind: "combo_window", ms: 3_000 },
    twistText: "Tight combos: the combo window is 3 s.",
  },
];

/** The twist values of one stage, in the form that the rules read. */
export interface StageTwistValues {
  /** The launch ball save in ms, or null when the stage keeps the table value. */
  readonly launchSaveMs: number | null;
  /** The scale of each timed mode. 1 when the stage has no mode twist. */
  readonly modeScale: number;
  /** The scale of ramp and orbit shots. 1 when the stage has no shot twist. */
  readonly shotScale: number;
  /** The combo window in ms, or null when the stage keeps the table value. */
  readonly comboMs: number | null;
}

/** Reads the current count of a goal kind from the signals of the stage. */
const COUNT_OF: Readonly<Record<StageGoalKind, (signals: RunSignals) => number>> = {
  score: (s) => s.stagePoints,
  modes: (s) => s.modesFinished,
  multiball: (s) => s.multiballs,
  lit_shots: (s) => s.litShots,
  jackpots: (s) => s.jackpots,
};

/** The count of a goal in the stage that runs now. */
export function goalCount(signals: RunSignals, goal: StageGoal): number {
  return COUNT_OF[goal.kind](signals);
}

/** True when the count of a goal has reached its target. */
export function goalMet(signals: RunSignals, goal: StageGoal): boolean {
  return goalCount(signals, goal) >= goal.target;
}

/** Maps one twist to the values that the rules read. A kind sets one field only. */
function twistValues(twist: StageTwist): StageTwistValues {
  const none: StageTwistValues = { launchSaveMs: null, modeScale: 1, shotScale: 1, comboMs: null };
  switch (twist.kind) {
    case "launch_save":
      return { ...none, launchSaveMs: twist.ms };
    case "mode_time":
      return { ...none, modeScale: twist.scale };
    case "shot_value":
      return { ...none, shotScale: twist.scale };
    case "combo_window":
      return { ...none, comboMs: twist.ms };
  }
}

/** The twist values of each catalog stage, computed once. The index is the stage index. */
const TWIST_VALUES: readonly StageTwistValues[] = STAGES.map((s) => twistValues(s.twist));

/** The twist values of a stage. A catalog stage gets the same object on every call. */
export function twistFor(stage: StageDef): StageTwistValues {
  const index = STAGES.indexOf(stage);
  return TWIST_VALUES[index] ?? twistValues(stage.twist);
}
